/**
 * What a frontend module may contribute: pages (each with its guard and its
 * menu entry) and components for named slots on core's screens.
 *
 * Type-only imports from core, so core may import this file — like the
 * backend's `modules/types.ts`, it must never pull a module in at run time.
 */
import type { ComponentType, ReactElement } from 'react';
import type { SvgIconComponent } from '@mui/icons-material';
import type { UserDto } from '@forsys/shared';
import type { CoreSectionId } from '../layout/navigation';
import type { MODULE_LOCALES } from './locales';

type ModuleCatalogues = typeof MODULE_LOCALES.en;

/** The keys of `T` whose value is a string — a label, not a nested object. */
type StringKeys<T> = Extract<{ [K in keyof T]: T[K] extends string ? K : never }[keyof T], string>;

/**
 * `<module>:nav.<item>` — a string in a module namespace's `nav` object.
 *
 * Derived from the module's English catalogue, so a menu entry with no
 * translation is a compile error, exactly as it is for core's. A module whose
 * catalogue has no `nav` object contributes no keys, and so cannot declare one.
 * `nav.section` is an object, so it is not a label.
 */
export type ModuleNavKey = {
  [NS in keyof ModuleCatalogues]: ModuleCatalogues[NS] extends { readonly nav: infer Nav }
    ? `${NS}:nav.${StringKeys<Nav>}`
    : never;
}[keyof ModuleCatalogues];

/** The section headings in each module catalogue's `nav.section`, by namespace. */
type SectionsOf<NS extends keyof ModuleCatalogues> = ModuleCatalogues[NS] extends {
  readonly nav: { readonly section: infer Sections };
}
  ? StringKeys<Sections>
  : never;

/** `<module>:nav.section.<id>` — the heading of a section a module declares. */
export type ModuleSectionKey = {
  [NS in keyof ModuleCatalogues]: `${NS}:nav.section.${SectionsOf<NS>}`;
}[keyof ModuleCatalogues];

/**
 * Every section id some module's catalogue has a heading for. Several modules
 * may have the same one — that is how two modules share a section.
 */
export type ModuleSectionId = { [NS in keyof ModuleCatalogues]: SectionsOf<NS> }[keyof ModuleCatalogues];

/** Where a page's menu entry may go: one of core's sections, or one a module declares. */
export type NavSectionId = CoreSectionId | ModuleSectionId;

/**
 * A sidebar section a module needs and core does not have — "Sales",
 * "Settings". It belongs to no module: every module that puts a page in it
 * declares it, and the first in `MODULES` order gives the heading and the
 * position. See `navigationWith` in `layout/navigation.ts`.
 */
export interface ModuleSection {
  readonly id: ModuleSectionId;
  /** In this module's own namespace, so the module stands alone. */
  readonly titleKey: ModuleSectionKey;
  /**
   * Position against core's sections, which are 100 (Overview), 300 (Content),
   * 400 (People), 500 (Notifications) and 600 (History).
   */
  readonly order: number;
}

/** A page's entry in the sidebar. */
export interface ModuleNav {
  /** A core section, or one this module declares in `sections`. */
  readonly section: NavSectionId;
  readonly labelKey: ModuleNavKey;
  readonly icon: SvgIconComponent;
  /** Highlight on the exact path only. See `NavItem.exact`. */
  readonly exact?: boolean;
}

export interface ModulePage {
  /** Relative to the root, like core's routes: `messages`, `messages/:id`. */
  readonly path: string;
  readonly element: ReactElement;
  /**
   * Holding any of these reaches the page. **The menu entry inherits it**, so
   * the route guard and the nav item cannot disagree — the rule core's
   * `App.tsx` and `navigation.ts` have to keep by hand.
   */
  readonly anyOf?: readonly string[];
  /** Omit for a page with no menu entry of its own, like a detail screen. */
  readonly nav?: ModuleNav;
}

/**
 * Every slot core renders, and what it hands its contributions.
 *
 * A slot is added here **when a module needs one**, in the same change that
 * places `<Slot>` on the screen. An empty slot "for later" is a speculative
 * hook — the thing this design avoids.
 */
export interface SlotProps {
  /** The header, left of the notification bell. Something that wants noticing. */
  'header.actions': Record<string, never>;
  /** A user's detail screen, beside the account's own actions. */
  'user.detail.actions': { readonly user: UserDto };
  /**
   * A user's detail screen, after the account and its memberships: what a
   * module knows about this person, each in its own `Panel`.
   */
  'user.detail.sections': { readonly user: UserDto };
  /**
   * The caller's own profile, below their details and password: a module's
   * preferences, each in its own `Panel`. The caller is `useAuth().user`.
   */
  'profile.sections': Record<string, never>;
  /**
   * The dashboard, between the stat tiles and the recent activity: what a
   * module has for the caller today, each in its own `Panel`. The caller is
   * `useAuth().user`. A contribution with nothing to say renders nothing.
   */
  'dashboard.sections': Record<string, never>;
}

export type SlotName = keyof SlotProps;

export interface SlotContribution<N extends SlotName> {
  /** Unique within the module; with the module's key, the React key. */
  readonly id: string;
  /** Rendered only for a caller holding any of these. Presentation, not protection. */
  readonly anyOf?: readonly string[];
  readonly component: ComponentType<SlotProps[N]>;
}

export type ModuleSlots = { readonly [N in SlotName]?: readonly SlotContribution<N>[] };

export interface FrontendModule {
  /** The backend module's key. Also its translation namespace. */
  readonly key: string;
  /** Every non-core section this module's pages use. */
  readonly sections?: readonly ModuleSection[];
  readonly pages?: readonly ModulePage[];
  readonly slots?: ModuleSlots;
}

/** Checks a manifest against `FrontendModule` where it is written. Returns it unchanged. */
export function defineFrontendModule<const M extends FrontendModule>(module: M): M {
  return module;
}
